import { useCallback, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { LinkAuthenticationElement, PaymentElement } from '@stripe/react-stripe-js';

import { useCheckout } from '../../../../hooks/use-checkout';
import { useAppConfig } from '../../../../hooks/use-app-config';
import { EBEPaymentMethodsName } from '../../../../contracts';
import { usePaymentState } from '../../hooks/use-payment-state';
import { useStripeSubmit } from '../../hooks/use-stripe-submit';
import { AddressElement } from '../AddressElement/AddressElement';
import { PayButton } from '../PaymentForm/components/PayButton/PayButton';
import { useAllowDefaultCheckSavedCardBox } from '../../../../hooks/use-allow-default-check-saved-card';
import { BlikStripePayment } from './components/BlikStripePayment/BlikStripePayment';
import { useAddressElementRef } from '../AddressElement/hooks/use-address-element-ref';
import { useShowTaxCalculationFields } from '../../hooks/use-show-tax-calculation-fields';
import { PaymentElementContainer } from '../PaymentElementContainer/PaymentElementContainer';
import { BackToSavedCardButton } from '../BackToSavedCardButton/BackToSavedCardButton';
import { Checkbox } from '../../../Checkbox/Checkbox';
import { logAlways } from '../../../../utils/coralogix-rum';
import { apmLatencyTracker } from '../../../../utils/apm-latency-tracker';

const APMS_WITHOUT_EMAIL_FIELD = [
  EBEPaymentMethodsName.card,
  EBEPaymentMethodsName.link,
  EBEPaymentMethodsName.ideal,
  EBEPaymentMethodsName.cashapp,
  EBEPaymentMethodsName.affirm,
  EBEPaymentMethodsName.eps,
  EBEPaymentMethodsName.bancontact,
  EBEPaymentMethodsName.amazonPay,
  EBEPaymentMethodsName.krCard,
  EBEPaymentMethodsName.payco,
  EBEPaymentMethodsName.samsungPay,
  EBEPaymentMethodsName.naverPay,
  EBEPaymentMethodsName.klarna,
];

interface StripePaymentElementProps {
  setOverlay: (value: boolean) => void;
  setApmTab: (value: any) => void;
  savedCardProps: {
    onBackButtonClicked: () => void;
    showBackButton: boolean;
  };
}

export function StripePaymentElement({
  setApmTab,
  setOverlay,
  savedCardProps,
}: StripePaymentElementProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { useTaxCalculationField, showAddressField } = useShowTaxCalculationFields();
  const { handleStripeSubmit } = useStripeSubmit({ setApmTab, setOverlay });
  const { isDefaultSaveCardAllowedForLocation } = useAllowDefaultCheckSavedCardBox();
  const {
    checkoutData,
    isCheckoutDataLoading,
    selectedPaymentMethod,
    selectedPaymentMethodDisplayNameRef,
  } = useCheckout();
  const { featureFlags } = useAppConfig();

  const setIsApmLoading = usePaymentState((state) => state.setIsApmLoading);
  const isPaymentLoading = usePaymentState((state) => state.isPaymentLoading);
  const setIsPaymentElementLoading = usePaymentState((state) => state.setIsPaymentElementLoading);
  const shouldSaveCard = usePaymentState((state) => state.shouldSaveCard || false);
  const setShouldSaveCard = usePaymentState((state) => state.setShouldSaveCard);
  const { AddressElementRef } = useAddressElementRef();

  const onReady = useCallback(() => {
    setIsPaymentElementLoading(false);
    setIsApmLoading(false);

    try {
      apmLatencyTracker.endTracking('Stripe', selectedPaymentMethod?.id, {
        paymentMethodName: selectedPaymentMethod?.paymentMethodName,
        paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
        provider: 'Stripe',
      });
    } catch (error) {
      console.error('Failed to end APM latency tracking:', error);
    }
  }, [setIsPaymentElementLoading, setIsApmLoading, selectedPaymentMethod]);

  const defaultEmail = checkoutData?.customer?.email || '';
  const isCard = selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.card;
  const isAffirm = selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.affirm;
  const isAfterpay = selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.afterpay;
  const isBlik = selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.blikCheckout;
  const hideStripePostalCode = isCard && useTaxCalculationField;

  const handleSubmit = () => {
    selectedPaymentMethodDisplayNameRef.current =
      selectedPaymentMethod?.paymentMethodDisplayName || '';
    handleStripeSubmit(false, selectedPaymentMethod?.id, AddressElementRef.current);
  };

  if (isBlik) {
    return <BlikStripePayment onReady={onReady} onSubmit={handleSubmit} />;
  }

  const isSaveCardAllowed = checkoutData?.checkoutConfigurations?.showSaveMyCard;
  const isNewSavedCardFlow =
    !checkoutData?.checkoutConfigurations?.showLinkSavedCard && isCard && isSaveCardAllowed;
  const shouldRenderLinkAuth =
    !isCheckoutDataLoading &&
    APMS_WITHOUT_EMAIL_FIELD.includes(
      selectedPaymentMethod?.paymentMethodName as EBEPaymentMethodsName,
    );

  const shouldHideEmailField = featureFlags?.checkout_enforce_customer_email;
  const saveCardBelowPayButton = featureFlags?.checkout_stripe_save_card_below_pay_button;

  // Initialize shouldSaveCard to true for new saved card flow if allowDefaultCheckSavedCardBox is true
  useEffect(() => {
    if (isNewSavedCardFlow && isDefaultSaveCardAllowedForLocation) {
      setShouldSaveCard(true);
    } else if (isNewSavedCardFlow && !isDefaultSaveCardAllowedForLocation) {
      setShouldSaveCard(false);
    }
  }, [isNewSavedCardFlow, setShouldSaveCard, isDefaultSaveCardAllowedForLocation]);

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Stripe', selectedPaymentMethod?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }
  }, [selectedPaymentMethod?.id]);

  return (
    <PaymentElementContainer
      content={
        <>
          <Box mb={1.5} style={{ pointerEvents: isPaymentLoading ? 'none' : 'auto' }}>
            {savedCardProps.showBackButton && (
              <Box mb={1.5}>
                <BackToSavedCardButton onClick={savedCardProps.onBackButtonClicked} />
              </Box>
            )}

            {shouldRenderLinkAuth && !shouldHideEmailField && (
              <LinkAuthenticationElement options={{ defaultValues: { email: defaultEmail } }} />
            )}
          </Box>
          <Stack spacing={1.5}>
            <PaymentElement
              key={selectedPaymentMethod?.id}
              onLoadError={({ error }) => {
                logAlways('Stripe Payment Element onLoadError', {
                  error: JSON.stringify(error),
                  checkoutData: JSON.stringify(checkoutData?.orderId),
                });
              }}
              options={{
                defaultValues: {
                  billingDetails: {
                    address: {
                      country:
                        checkoutData?.customer?.taxCountry ||
                        checkoutData?.customer?.geoLocator?.countryCode2,
                    },
                  },
                },
                readOnly: isPaymentLoading,
                wallets: {
                  googlePay: 'never',
                  applePay: 'never',
                  // workaround for save card feature
                  link: isNewSavedCardFlow ? 'never' : 'auto',
                } as any,
                fields: {
                  billingDetails: {
                    address: {
                      country: isAffirm || isAfterpay ? 'auto' : 'never',
                      postalCode: hideStripePostalCode ? 'never' : 'auto',
                    },
                  },
                },
              }}
              onReady={onReady}
            />

            {useTaxCalculationField && (
              <AddressElement ref={AddressElementRef} zipOnly={!showAddressField} />
            )}
            {isNewSavedCardFlow && !saveCardBelowPayButton && (
              <Box pt={0.5}>
                <Checkbox
                  label={t('saveCardCheckbox')}
                  checkboxProps={{
                    checked: shouldSaveCard,
                    onChange: (e) => setShouldSaveCard(!!(e.target as HTMLInputElement).checked),
                    disabled: isPaymentLoading,
                  }}
                />
              </Box>
            )}
          </Stack>
        </>
      }
    >
      <PayButton onClick={handleSubmit}>
        {isNewSavedCardFlow && saveCardBelowPayButton && (
          <Box pt={0.5}>
            <Checkbox
              label={t('saveCardCheckbox')}
              checkboxProps={{
                checked: shouldSaveCard,
                onChange: (e) => setShouldSaveCard(!!(e.target as HTMLInputElement).checked),
                disabled: isPaymentLoading,
              }}
            />
          </Box>
        )}
      </PayButton>
    </PaymentElementContainer>
  );
}
